
import React, { useState } from 'react'
import { motion } from 'motion/react'
import { Link } from '@tanstack/react-router'
import { useCanvas } from '@/hooks/useCanvas'
import { useSharedProjects } from '@/providers/ProjectsProvider'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Icon } from '@/components/ui/icon'
import { Button } from '@/components/ui/button'
import { CanvasCard, CanvasCardSkeleton } from '@/components/workspace/card'
import { useCanvasModals } from '@/components/workspace/hooks'
import {
  CanvasDeletePermanentModal,
  CanvasRestoreModal,
} from '@/components/workspace/modals'

export function TrashPage() {
  const {
    trashedCanvases,
    isTrashLoading,
    error,
    restoreCanvas,
    deleteCanvas,
    fetchTrash,
    emptyTrash,
  } = useCanvas()

  const [isEmptying, setIsEmptying] = useState(false)
  const [showEmptyConfirm, setShowEmptyConfirm] = useState(false)

  const { projects } = useSharedProjects()

  // Fetch trash when component mounts
  React.useEffect(() => {
    fetchTrash()
  }, [fetchTrash])

  const handleDeletePermanent = React.useCallback(async (canvasId: string) => {
    await deleteCanvas(canvasId, true) // Permanent delete
  }, [deleteCanvas])

  const noop = React.useCallback(async () => {}, [])

  const {
    handleAction,
    closeModal,
    modalHandlers,
    modalProps,
  } = useCanvasModals({
    projects,
    onRename: noop, // Not used in trash
    onMove: noop, // Not used in trash
    onDelete: noop, // Not used - we use onDeletePermanent
    onDeletePermanent: handleDeletePermanent,
    onDuplicate: noop, // Not used in trash
    onRestore: restoreCanvas,
  })

  return (
    <main className="h-full overflow-hidden">
      <div className="h-full overflow-y-auto">
        <div className="w-full space-y-6">
          {/* Header Actions */}
          <div className="flex items-center justify-end">
            {!isTrashLoading && trashedCanvases.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                disabled={isEmptying}
                onClick={() => setShowEmptyConfirm(true)}
              >
                <Icon name="trash-2" className="mr-1 h-4 w-4" />
                {isEmptying ? 'Emptying...' : 'Empty Trash'}
              </Button>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load deleted sessions: {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Canvas Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {isTrashLoading
              ? [...Array(12)].map((_, index) => (
                  <CanvasCardSkeleton key={`skeleton-${index}`} />
                ))
              : trashedCanvases.map((canvas, index) => (
                  <CanvasCard
                    key={canvas.id}
                    canvas={canvas}
                    contextMenuType="trash"
                    onAction={handleAction}
                    showProject={true}
                    index={index}
                  />
                ))}
          </div>

          {/* Empty State */}
          {!isTrashLoading && trashedCanvases.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="flex flex-col items-center pt-[33vh]"
            >
              <div className="text-center">
                <Icon name="trash-2" className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-sm font-semibold">Trash is empty</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Deleted sessions will appear here.
                </p>
                <Link to="/workspace/recents" className="mt-4 inline-block">
                  <Button variant="default" size="sm">
                    <Icon name="arrow-left" className="mr-1 h-4 w-4" />
                    Back to Sessions
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}

          {/* Modals - Only restore and permanent delete for trash */}
          <CanvasRestoreModal
            isOpen={modalProps.isRestoreOpen}
            onClose={closeModal}
            canvas={modalProps.canvas}
            onRestore={modalHandlers.onRestore}
          />

          <CanvasDeletePermanentModal
            isOpen={modalProps.isDeletePermanentOpen}
            onClose={closeModal}
            canvas={modalProps.canvas}
            onDeletePermanent={modalHandlers.onDeletePermanent}
          />

          {/* Empty Trash Confirmation */}
          <AlertDialog open={showEmptyConfirm} onOpenChange={(open) => { if (!isEmptying) setShowEmptyConfirm(open) }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                  <Icon name="alert-triangle" className="h-5 w-5" />
                  Empty Trash
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Permanently delete all {trashedCanvases.length} item{trashedCanvases.length !== 1 ? 's' : ''} in trash?
                  <br />
                  <span className="font-medium text-destructive">
                    This action cannot be undone.
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isEmptying}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  disabled={isEmptying}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90 focus:ring-destructive"
                  onClick={async () => {
                    setIsEmptying(true)
                    try {
                      await emptyTrash()
                    } finally {
                      setIsEmptying(false)
                      setShowEmptyConfirm(false)
                    }
                  }}
                >
                  {isEmptying ? (
                    <>
                      <Icon name="loader-2" className="mr-2 h-4 w-4 animate-spin" />
                      Emptying...
                    </>
                  ) : (
                    <>
                      <Icon name="trash-2" className="mr-2 h-4 w-4" />
                      Empty Trash
                    </>
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </main>
  )
}
